import { ApolloClient, HttpLink, InMemoryCache, type NormalizedCacheObject } from '@apollo/client'
import type { Operation } from '@apollo/client'

import { buildItManagementMarketplaceServiceGraphqlUrl } from 'modules/core/lib/buildUrl'

let marketplaceClient: ApolloClient<NormalizedCacheObject> | null = null

function ensureMarketplaceGraphqlUriConfigured(uri: string, operationName?: string): string {
  const trimmedUri = uri.trim()

  if (trimmedUri.length > 0) {
    return trimmedUri
  }

  const opSuffix = operationName ? ` (operation: ${operationName})` : ''

  throw new Error(
    `IT Management Marketplace service GraphQL endpoint is not configured${opSuffix}. ` +
      'Set it_management_marketplace_service_graphql_endpoint in environment.json. ' +
      'This service is mandatory and must not fallback to monolith/backend endpoints.'
  )
}

function buildMarketplaceGraphqlUri(operation: Operation): string {
  const baseUri = ensureMarketplaceGraphqlUriConfigured(
    buildItManagementMarketplaceServiceGraphqlUrl(),
    operation.operationName
  )
  const operationName = operation.operationName?.trim()

  if (!operationName) {
    return baseUri
  }

  const separator = baseUri.includes('?') ? '&' : '?'
  return `${baseUri}${separator}${encodeURIComponent(operationName)}`
}

/**
 * Builds or returns the singleton Apollo client for the IT Management Marketplace
 * cell service GraphQL API. Used for cart operations (create cart, add/update/remove
 * lines) that are stored by the cell service.
 */
export function getMarketplaceGraphqlClient(): ApolloClient<NormalizedCacheObject> {
  if (!marketplaceClient) {
    marketplaceClient = new ApolloClient({
      link: new HttpLink({
        uri: buildMarketplaceGraphqlUri,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      cache: new InMemoryCache(),
      defaultOptions: {
        query: { fetchPolicy: 'network-only' },
        mutate: { fetchPolicy: 'network-only' },
      },
    })
  }
  return marketplaceClient
}
